import round from 'lodash/round';

import {
  Column,
  ColumnGroup,
  DataTable,
  TableHeader,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { ExportFileButton } from 'components/buttons';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';

import { CostReportTableData } from './types';
import { NodePricingInfo } from '../../../../../../cost-report/components/NodePricingInfo';
import { RequestedResourceCell } from '../../../../_components/RequestedResourceCell';

type DailyComputeSpendTableProps = {
  data: CostReportTableData[];
  isLoading: boolean;
  hasGpuData: boolean;
  hasTpuData: boolean;
  showStorage: boolean;
  cacheKey: string;
  urlKey?: string;
  testId: string;
  onDownload?: () => void;
};

export const DailyComputeSpendTable = ({
  data,
  isLoading,
  hasGpuData,
  hasTpuData,
  showStorage,
  cacheKey,
  urlKey,
  testId,
  onDownload,
}: DailyComputeSpendTableProps) => {
  return (
    <DataTable
      maxRows={31}
      data={data}
      isLoading={isLoading}
      outerHeader={
        <TableHeader
          recordsCount="Daily compute spend details"
          recordsCountWrapperProps={{ typography: 'A2' }}
          actions={
            onDownload ? (
              <ExportFileButton
                onClick={onDownload}
                testId="download-csv-report"
              />
            ) : undefined
          }
        />
      }
      cacheKey={cacheKey}
      urlKey={urlKey}
      testId={testId}
    >
      <Column id="date" header="Date" width={170} sortable />
      <Column<CostReportTableData>
        id="pods"
        header="Pods"
        minWidth={89}
        renderCell={({ podCount }) => round(podCount?.total || 0, 0)}
        sortable
        accessor="podCount.total"
      />
      <Column<CostReportTableData>
        id="costPerPod"
        header="Cost per pod"
        minWidth={149}
        sortable
        renderCell={({ normalizedCostPerPod }) => (
          <span data-sortweight={normalizedCostPerPod}>
            {formatDailyMetricsCost(normalizedCostPerPod)}
          </span>
        )}
      />
      <ColumnGroup id="requested-resources" title="Requested resources">
        <Column<CostReportTableData>
          id="totalCpuCount"
          header="CPU"
          minWidth={128}
          renderCell={({ countCpu }) => <RequestedResourceCell {...countCpu} />}
          sortable
          accessor="countCpu.total"
          sortingType="number"
        />
        <Column<CostReportTableData>
          id="totalRamCount"
          header="Memory"
          minWidth={128}
          renderCell={({ countRamGib }) => (
            <RequestedResourceCell {...countRamGib} />
          )}
          sortable
          accessor="countRamGib.total"
          sortingType="number"
        />
        {hasGpuData && (
          <Column<CostReportTableData>
            id="totalGpuCount"
            header="GPU"
            minWidth={128}
            renderCell={({ countGpu }) => (
              <RequestedResourceCell {...countGpu} />
            )}
            sortable
            accessor="countGpu.total"
            sortingType="number"
          />
        )}
        {hasTpuData && (
          <Column<CostReportTableData>
            id="totalTpuCount"
            header="TPU"
            minWidth={128}
            sortingType="number"
            headerCellSx={{ textTransform: 'none' }}
            renderCell={({ countTpu }) => {
              return <RequestedResourceCell {...countTpu} />;
            }}
            sortable
            accessor="countTpu.total"
          />
        )}
        {showStorage && (
          <Column<CostReportTableData>
            id="totalStorageCount"
            header="Storage"
            minWidth={128}
            renderCell={({ countStorageGib }) => (
              <>
                {countStorageGib.total ? (
                  <Typography variant="inherit">
                    {countStorageGib.total.toFixed(2)}
                    <Typography
                      variant="inherit"
                      component="span"
                      color={tokens.theme.fg.structural.secondary}
                      pl={4}
                    >
                      GiB
                    </Typography>
                  </Typography>
                ) : (
                  '-'
                )}
              </>
            )}
            sortable
            accessor="countStorageGib.total"
            sortingType="number"
          />
        )}
      </ColumnGroup>
      <ColumnGroup id="total-cost" title="Total cost per resource">
        <Column<CostReportTableData>
          id="totalCpuCost"
          header="CPU"
          minWidth={104}
          renderCell={({ totalUptimeCpuCost }) =>
            formatDailyMetricsCost(totalUptimeCpuCost)
          }
          sortable
          accessor="totalUptimeCpuCost"
          sortingType="number"
        />
        <Column<CostReportTableData>
          id="totalRamCost"
          header="Memory"
          minWidth={104}
          renderCell={({ totalUptimeRamCost }) =>
            formatDailyMetricsCost(totalUptimeRamCost)
          }
          sortable
          accessor="totalUptimeRamCost"
          sortingType="number"
        />
        {hasGpuData && (
          <Column<CostReportTableData>
            id="totalGpuCost"
            header="GPU"
            minWidth={104}
            renderCell={({ totalUptimeGpuCost }) =>
              formatDailyMetricsCost(totalUptimeGpuCost)
            }
            sortable
            accessor="totalUptimeGpuCost"
            sortingType="number"
          />
        )}
        {hasTpuData && (
          <Column<CostReportTableData>
            id="totalTpuCost"
            header="TPU"
            minWidth={104}
            sortable
            sortingType="number"
            headerCellSx={{ textTransform: 'none' }}
            renderCell={({ totalUptimeTpuCost }) =>
              formatDailyMetricsCost(totalUptimeTpuCost)
            }
            accessor="totalUptimeTpuCost"
          />
        )}
        {showStorage && (
          <Column<CostReportTableData>
            id="totalStorageCost"
            header="Storage"
            minWidth={128}
            renderCell={({ totalUptimeStorageCost }) =>
              formatDailyMetricsCost(totalUptimeStorageCost)
            }
            sortable
            accessor="totalUptimeStorageCost"
            sortingType="number"
          />
        )}
      </ColumnGroup>
      <Column<CostReportTableData>
        id="totalCostDistribution"
        header="Total cost"
        minWidth={194}
        renderCell={({ totalUptimeCost }) => {
          return (
            <Stack>
              <Typography variant="inherit" fontWeight="bold" noWrap>
                {formatDailyMetricsCost(totalUptimeCost.total)}
              </Typography>
              <NodePricingInfo
                {...totalUptimeCost}
                showStorage={showStorage}
                progressBarSx={{ height: '4px' }}
                sx={{ lineHeight: '4px' }}
                hideTotal
              />
            </Stack>
          );
        }}
        sortable
        accessor="totalUptimeCost.total"
        sortingType="number"
      />
    </DataTable>
  );
};
